import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { MagicSearchPalette } from '@components/magic-search-palette';
import { UserContext } from '@contexts/user/user-context';
import { useFeatureFlag } from '@posthog-app/use-feature-flag';

import { MagicSearchContext } from './magic-search-context';
import { useMagicSearchShortcut } from './use-magic-search-shortcut';

/**
 * Gated on `isSuperAdmin` and the `magic-search` flag, the flag being the kill switch. While impersonating, `/v1/me`
 * answers as the customer, so the palette hides: its endpoint would refuse the customer's token anyway. The gate only
 * hides; the endpoint authorises every request itself.
 */
export const MagicSearchProvider = ({ children }: { children: ReactNode }) => {
  const { userState } = useContext(UserContext);
  const isFlagOn = useFeatureFlag('magic-search');
  const isAvailable = isFlagOn && !!userState?.isSuperAdmin;
  const [isOpen, setIsOpen] = useState(false);

  const openMagicSearch = useCallback(() => setIsOpen(true), []);
  const closeMagicSearch = useCallback(() => setIsOpen(false), []);
  const toggleMagicSearch = useCallback(() => setIsOpen((wasOpen) => !wasOpen), []);

  useMagicSearchShortcut({ isEnabled: isAvailable, onToggle: toggleMagicSearch });

  const value = useMemo(() => ({ isAvailable, openMagicSearch }), [isAvailable, openMagicSearch]);

  return (
    <MagicSearchContext.Provider value={value}>
      {children}
      {isAvailable && <MagicSearchPalette isOpen={isOpen} onClose={closeMagicSearch} />}
    </MagicSearchContext.Provider>
  );
};
